import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const AdminLogin = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            // Llamada a la función RPC segura de Supabase
            const { data, error: rpcError } = await supabase
                .rpc('verificar_admin', {
                    p_usuario: username,
                    p_password: password
                });

            if (rpcError) throw rpcError;

            // La función RPC retorna un array de resultados (o vacío si no coincide)
            if (data && data.length > 0 && data[0].exito) {
                // Login Exitoso
                const adminUser = data[0];
                localStorage.setItem('plasa_admin_session', JSON.stringify({
                    id: adminUser.id,
                    usuario: adminUser.usuario,
                    timestamp: new Date().getTime()
                }));

                // Registrar actividad de login (RPC)
                await supabase.rpc('registrar_login', { p_usuario: adminUser.usuario });

                navigate('/admin/dashboard');
            } else {
                setError('Credenciales inválidas');
            }
        } catch (err) {
            console.error('Error de login:', err);
            setError('Error al conectar con el servidor');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-black font-sans relative overflow-hidden">
            {/* Background Image with Overlay */}
            <div className="absolute inset-0 z-0">
                <img
                    src="/admin-bg.png"
                    alt="Admin Background"
                    className="w-full h-full object-cover opacity-60"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-zinc-950/80 to-transparent"></div>
                <div className="absolute inset-0 bg-red-900/10 mix-blend-overlay"></div>
            </div>

            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 30 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="relative z-10 w-full max-w-md p-8 bg-zinc-900/80 backdrop-blur-xl border border-zinc-700/50 rounded-2xl shadow-2xl overflow-hidden"
            >
                {/* Glow Effect */}
                <div className="absolute -top-20 -left-20 w-40 h-40 bg-red-600 rounded-full blur-[100px] opacity-20 pointer-events-none"></div>
                <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-red-600 rounded-full blur-[100px] opacity-20 pointer-events-none"></div>

                <div className="mb-8 text-center relative z-20">
                    <motion.div
                        initial={{ y: -20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2, duration: 0.5 }}
                    >
                        <h1 className="text-4xl font-black tracking-tighter text-white mb-2 drop-shadow-lg">
                            PLASA <span className="text-red-600">ADMIN</span>
                        </h1>
                        <p className="text-zinc-400 text-sm font-medium">Sistema de Gestión de Manufactura</p>
                    </motion.div>
                </div>

                <form onSubmit={handleLogin} className="space-y-5 relative z-20">
                    <motion.div
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.3 }}
                    >
                        <label className="block text-xs uppercase tracking-wider text-red-500 font-bold mb-2 ml-1">Usuario</label>
                        <input
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="w-full bg-black/50 border border-zinc-700 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-all duration-300"
                        />
                    </motion.div>

                    <motion.div
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.4 }}
                    >
                        <label className="block text-xs uppercase tracking-wider text-red-500 font-bold mb-2 ml-1">Contraseña</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full bg-black/50 border border-zinc-700 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-all duration-300"
                        />
                    </motion.div>

                    <AnimatePresence>
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, height: 0, scale: 0.9 }}
                                animate={{ opacity: 1, height: 'auto', scale: 1 }}
                                exit={{ opacity: 0, height: 0, scale: 0.9 }}
                                className="text-red-400 text-sm text-center font-bold bg-red-950/40 border border-red-900/50 p-2 rounded-lg"
                            >
                                {error}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <motion.button
                        type="submit"
                        disabled={loading}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.5 }}
                        className="w-full bg-gradient-to-r from-red-700 to-red-600 text-white font-bold py-4 rounded-xl hover:from-red-600 hover:to-red-500 shadow-lg shadow-red-900/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wide"
                    >
                        {loading ? (
                            <span className="flex items-center justify-center gap-2">
                                <span className="w-2 h-2 bg-white rounded-full animate-bounce"></span>
                                <span className="w-2 h-2 bg-white rounded-full animate-bounce [animation-delay:-.3s]"></span>
                                <span className="w-2 h-2 bg-white rounded-full animate-bounce [animation-delay:-.5s]"></span>
                            </span>
                        ) : 'Acceder al Panel'}
                    </motion.button>
                </form>
            </motion.div>
        </div>
    );
};

export default AdminLogin;
